import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const REQUESTED_ITEMS_TABS = [
  'needs_details',
  'open_chats',
  'completed',
] as const;
export type RequestedItemsTab = (typeof REQUESTED_ITEMS_TABS)[number];

const QUERY_PARAM_Q = 'q';
const QUERY_PARAM_TAB = 'tab';
const QUERY_PARAM_PAGE = 'page';
const QUERY_PARAM_OPEN_CHAT = 'openChat';
const QUERY_PARAM_STYLE_COLOR = 'styleColor';

const DEFAULT_TAB: RequestedItemsTab = 'needs_details';
const DEFAULT_PAGE = 1;

/**
 * Gets a query param value case-insensitively.
 * Uses the canonical key (e.g. from QUERY_PARAM_* constant) to match against URL keys.
 */
function getParamCaseInsensitive(
  params: URLSearchParams,
  canonicalKey: string,
): string | null {
  const lower = canonicalKey.toLowerCase();
  const keys = Array.from(params.keys());
  const key = keys.find((k) => k.toLowerCase() === lower);
  return key !== undefined ? (params.get(key) ?? null) : null;
}

/**
 * Deletes a query param case-insensitively (removes the key as it appears in the URL).
 */
function deleteParamCaseInsensitive(
  params: URLSearchParams,
  canonicalKey: string,
): void {
  const lower = canonicalKey.toLowerCase();
  const keysToDelete = Array.from(params.keys()).filter(
    (key) => key.toLowerCase() === lower,
  );
  keysToDelete.forEach((key) => params.delete(key));
}

function parseTab(value: string | null): RequestedItemsTab {
  if (
    value === 'needs_details' ||
    value === 'open_chats' ||
    value === 'completed'
  ) {
    return value;
  }
  return DEFAULT_TAB;
}

function parsePage(value: string | null): number {
  const n = value ? parseInt(value, 10) : NaN;
  return Number.isInteger(n) && n >= 1 ? n : DEFAULT_PAGE;
}

function parseOpenChat(value: string | null): boolean {
  if (value === null || value === '') return false;
  const v = value.toLowerCase();
  return v === '1' || v === 'true';
}

function parseStyleColorForChat(value: string | null): string | null {
  if (value === null || value === '') return null;
  try {
    const decoded = decodeURIComponent(value);
    return decoded.length > 0 ? decoded : null;
  } catch {
    return null;
  }
}

export interface RequestedItemsUrlParams {
  search: string;
  tab: RequestedItemsTab;
  page: number;
  openChat: boolean;
  styleColorForChat: string | null;
  setSearch: (value: string) => void;
  setTab: (tab: RequestedItemsTab) => void;
  setPage: (page: number) => void;
  updateUrl: (updates: {
    q?: string;
    tab?: RequestedItemsTab;
    page?: number;
  }) => void;
  clearOpenChatParams: () => void;
}

/**
 * Single source of truth for RequestedItems view state synced to URL.
 * Query params: q (search), tab, page. Other params (e.g. sort) are preserved.
 */
export function useRequestedItemsUrlParams(): RequestedItemsUrlParams {
  const location = useLocation();
  const history = useHistory();

  const { search, tab, page, openChat, styleColorForChat } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      search: getParamCaseInsensitive(params, QUERY_PARAM_Q) ?? '',
      tab: parseTab(getParamCaseInsensitive(params, QUERY_PARAM_TAB)),
      page: parsePage(getParamCaseInsensitive(params, QUERY_PARAM_PAGE)),
      openChat: parseOpenChat(
        getParamCaseInsensitive(params, QUERY_PARAM_OPEN_CHAT),
      ),
      styleColorForChat: parseStyleColorForChat(
        getParamCaseInsensitive(params, QUERY_PARAM_STYLE_COLOR),
      ),
    };
  }, [location.search]);

  const updateUrl = useCallback(
    (updates: { q?: string; tab?: RequestedItemsTab; page?: number }) => {
      const params = new URLSearchParams(location.search);
      if (updates.q !== undefined) {
        deleteParamCaseInsensitive(params, QUERY_PARAM_Q);
        if (updates.q !== '') {
          params.set(QUERY_PARAM_Q, updates.q);
        }
      }
      if (updates.tab !== undefined) {
        deleteParamCaseInsensitive(params, QUERY_PARAM_TAB);
        params.set(QUERY_PARAM_TAB, updates.tab);
      }
      if (updates.page !== undefined) {
        deleteParamCaseInsensitive(params, QUERY_PARAM_PAGE);
        if (updates.page !== 1) {
          params.set(QUERY_PARAM_PAGE, String(updates.page));
        }
      }
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [history, location.pathname, location.search],
  );

  const setSearch = useCallback(
    (value: string) => {
      updateUrl({ q: value, page: 1 });
    },
    [updateUrl],
  );

  const setTab = useCallback(
    (newTab: RequestedItemsTab) => {
      updateUrl({ tab: newTab, page: 1 });
    },
    [updateUrl],
  );

  const setPage = useCallback(
    (newPage: number) => {
      updateUrl({ page: newPage });
    },
    [updateUrl],
  );

  const clearOpenChatParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    deleteParamCaseInsensitive(params, QUERY_PARAM_OPEN_CHAT);
    deleteParamCaseInsensitive(params, QUERY_PARAM_STYLE_COLOR);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [history, location.pathname, location.search]);

  return {
    search,
    tab,
    page,
    openChat,
    styleColorForChat,
    setSearch,
    setTab,
    setPage,
    updateUrl,
    clearOpenChatParams,
  };
}
